import { useCallback, useEffect, useState } from 'react';
import { toast } from 'sonner';
import { UserPlus, Users, Trash2, Copy, RefreshCw } from 'lucide-react';
import { supabase } from '../supabaseClient';
import { formatDateTime } from '../lib/utils';
import { useCached, invalidate } from '../lib/cache';
import PageHeader from './ui/PageHeader';
import { Card, CardHeader, CardTitle, CardDescription, CardBody } from './ui/Card';
import { Input, Label } from './ui/Input';
import Button from './ui/Button';
import Badge from './ui/Badge';
import { SkeletonRow } from './ui/Skeleton';

/**
 * Plan v8/E6 (16.10): ZESPÓŁ organizacji — kto z agencji (albo Mediafy) pracuje w MAMS.
 * Każdy członek widzi wszystkich klientów organizacji (w Klienci może zawęzić do „Moich”).
 * Zaprasza / odpina tylko właściciel organizacji (albo admin MAMS). Hasło nowego konta pokazujemy RAZ.
 */
const ORG_TEAM_URL = `${process.env.REACT_APP_SUPABASE_URL}/functions/v1/org-team`;

async function call(body) {
  const { data: { session } } = await supabase.auth.getSession();
  const res = await fetch(ORG_TEAM_URL, { method: 'POST', headers: { 'Content-Type': 'application/json', Authorization: `Bearer ${session?.access_token}` }, body: JSON.stringify(body) });
  const d = await res.json().catch(() => ({}));
  if (!res.ok) throw new Error(d.error || `HTTP ${res.status}`);
  return d;
}

// T2.2: plan organizacji i zużycie w bieżącym miesiącu (RPC my_org_usage) — agencja widzi, za co zapłaci.
function PlanCard({ org }) {
  // PERF-UX: plan i zużycie z cache (świeże po 60 s w tle) — karta jest od razu przy drugim wejściu.
  const { data: info } = useCached(`team:plan:${org.id}`, async () => {
    const month = new Date().toISOString().slice(0, 7) + '-01';
    const [{ data: o }, { data: u }] = await Promise.all([
      supabase.from('organizations').select('plan, credits, renews_at, plans(name, monthly_credits, renews, price_pln)').eq('id', org.id).maybeSingle(),
      supabase.rpc('my_org_usage', { p_month: month }),
    ]);
    return { o: o || null, u: (u || [])[0] || null };
  }, { staleMs: 60_000 });
  if (!info?.o || info.o.plan === 'mediafy') return null;
  const p = info.o.plans || {};
  return (
    <Card className="mb-5">
      <CardHeader>
        <CardTitle>Plan: {p.name || info.o.plan}</CardTitle>
        <CardDescription>
          {p.renews ? `${p.monthly_credits} kredytów co miesiąc` : `${p.monthly_credits || 20} kredytów na start (jednorazowo)`} · 1 kredyt = 1 produkt na 1 kanale
          {info.o.renews_at ? ` · następne odnowienie ${formatDateTime(info.o.renews_at)}` : ''}
        </CardDescription>
      </CardHeader>
      <CardBody className="grid grid-cols-2 sm:grid-cols-4 gap-3 text-sm">
        <div><div className="text-2xs uppercase text-ink-500">Saldo</div><div className="text-lg font-semibold tabular-nums">{info.o.credits}</div></div>
        <div><div className="text-2xs uppercase text-ink-500">Zużyte w tym mies.</div><div className="text-lg font-semibold tabular-nums">{info.u?.credits_net ?? 0}</div></div>
        <div><div className="text-2xs uppercase text-ink-500">Produkty (SKU)</div><div className="text-lg font-semibold tabular-nums">{info.u?.sku ?? 0}</div></div>
        <div><div className="text-2xs uppercase text-ink-500">Zlecenia</div><div className="text-lg font-semibold tabular-nums">{info.u?.orders ?? 0}</div></div>
        {!p.renews && <p className="col-span-full text-xs text-ink-500">Więcej kredytów albo plan miesięczny: napisz na kontakt@mediafy.com.pl — fakturę wystawiamy co miesiąc.</p>}
      </CardBody>
    </Card>
  );
}

export default function TeamPage({ org }) {
  const [email, setEmail] = useState('');
  const [busy, setBusy] = useState(false);
  const [created, setCreated] = useState(null); // { email, password }

  // PERF-UX A3 (21.10): lista zespołu z RPC `org_team_members` (t58, jedno zapytanie ~100 ms) zamiast edge `org-team list`
  // (zimny start + getUserById per członek = 1,5–6 s). Z cache: od razu, odświeżenie w tle; po zaproszeniu/odpięciu `invalidate`.
  const { data, error, refresh, refreshing } = useCached(`team:members:${org.id}`, async () => {
    const { data: rows, error: e } = await supabase.rpc('org_team_members', { p_org: org.id });
    if (e) throw e;
    return { members: (rows || []).map(r => ({ user_id: r.user_id, role: r.role, email: r.email, last_sign_in_at: r.last_sign_in_at })), can_manage: !!(rows || [])[0]?.can_manage };
  }, { staleMs: 60_000 });
  useEffect(() => { if (error) toast.error(`Zespół: ${error.message}`); }, [error]);
  const load = useCallback(() => { invalidate(`team:members:${org.id}`); return refresh(true); }, [org.id, refresh]);

  const invite = async () => {
    setBusy(true);
    try {
      const r = await call({ action: 'invite', org_id: org.id, email: email.trim() });
      if (r.password) setCreated({ email: r.email, password: r.password });
      else toast.success(`${r.email} dołączył(a) do zespołu (istniejące konto — loguje się swoim hasłem)`);
      setEmail('');
      load();
    } catch (e) { toast.error(e.message); }
    finally { setBusy(false); }
  };

  const remove = async (m) => {
    if (!window.confirm(`Odpiąć ${m.email} od zespołu ${org.name}? Konto zostaje, traci dostęp do klientów organizacji.`)) return;
    try { await call({ action: 'remove', org_id: org.id, user_id: m.user_id }); load(); }
    catch (e) { toast.error(e.message); }
  };

  return (
    <div>
      <PageHeader
        title="Zespół"
        subtitle={`${org.name} — kto pracuje w MAMS. Każdy widzi wszystkich klientów organizacji; w Klienci można zawęzić widok do „Moich”.`}
        action={<Button variant="ghost" onClick={load} loading={refreshing} leftIcon={<RefreshCw className="w-4 h-4" />}>Odśwież</Button>}
      />
      <PlanCard org={org} />

      {created && (
        <Card className="mb-4 border-success/30">
          <CardBody>
            <div className="text-sm font-semibold text-ink-900 dark:text-ink-50">Konto założone: {created.email}</div>
            <div className="text-xs text-ink-500 dark:text-ink-400 mt-1">Hasło pokazujemy tylko teraz — przekaż je pracownikowi; zmieni je przez „Nie pamiętam hasła”.</div>
            <div className="mt-2 flex items-center gap-2">
              <code className="px-2 py-1 rounded bg-ink-100 dark:bg-ink-800 text-sm select-all">{created.password}</code>
              <Button size="sm" variant="ghost" leftIcon={<Copy className="w-3.5 h-3.5" />} onClick={() => { navigator.clipboard?.writeText(`${created.email} / ${created.password}`); toast.success('Skopiowano'); }}>Kopiuj</Button>
              <Button size="sm" variant="ghost" onClick={() => setCreated(null)}>Ukryj</Button>
            </div>
          </CardBody>
        </Card>
      )}

      {data?.can_manage && (
        <Card className="mb-4">
          <CardHeader>
            <CardTitle>Dodaj pracownika</CardTitle>
            <CardDescription>Nowy e-mail = nowe konto z hasłem jednorazowym. Istniejące konto = dopięte do zespołu.</CardDescription>
          </CardHeader>
          <CardBody>
            <div className="flex items-end gap-3 max-w-xl">
              <div className="flex-1">
                <Label>E-mail pracownika</Label>
                <Input value={email} onChange={(e) => setEmail(e.target.value)} placeholder="jan@agencja.pl" onKeyDown={(e) => { if (e.key === 'Enter' && email.trim()) invite(); }} />
              </div>
              <Button onClick={invite} loading={busy} disabled={!email.trim()} leftIcon={<UserPlus className="w-4 h-4" />}>Dodaj</Button>
            </div>
          </CardBody>
        </Card>
      )}

      {data == null ? (
        <div className="grid gap-3"><SkeletonRow /><SkeletonRow /></div>
      ) : (
        <Card className="overflow-hidden">
          <table className="w-full text-sm">
            <thead className="bg-ink-50 dark:bg-ink-800/50 text-2xs uppercase tracking-wide text-ink-500 dark:text-ink-400">
              <tr>
                <th className="text-left px-5 py-3 font-bold">Osoba</th>
                <th className="text-left px-5 py-3 font-bold">Rola</th>
                <th className="text-left px-5 py-3 font-bold">Ostatnie logowanie</th>
                <th className="px-5 py-3" />
              </tr>
            </thead>
            <tbody className="divide-y divide-ink-100 dark:divide-ink-800">
              {data.members.length === 0 && (
                <tr><td colSpan={4} className="px-5 py-6 text-center text-ink-400"><Users className="w-5 h-5 inline mr-2" />Brak członków</td></tr>
              )}
              {data.members.map((m) => (
                <tr key={m.user_id}>
                  <td className="px-5 py-3 font-medium text-ink-900 dark:text-ink-50">{m.email || m.user_id}</td>
                  <td className="px-5 py-3"><Badge variant={m.role === 'owner' ? 'brand' : 'neutral'} size="sm">{m.role === 'owner' ? 'właściciel' : 'pracownik'}</Badge></td>
                  <td className="px-5 py-3 text-xs text-ink-500 dark:text-ink-400">{m.last_sign_in_at ? formatDateTime(m.last_sign_in_at) : 'jeszcze nie'}</td>
                  <td className="px-5 py-3 text-right">
                    {data.can_manage && m.role !== 'owner' && (
                      <Button size="sm" variant="ghost" onClick={() => remove(m)} leftIcon={<Trash2 className="w-3.5 h-3.5" />}>Odepnij</Button>
                    )}
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        </Card>
      )}
    </div>
  );
}
